// The thread list's filter views and the words each one is shown under. Both
// apps offer the same five views and must name them the same way, so the union
// and its label tables are compiled from this one file. The icons are not here:
// web draws lucide glyphs and native draws SF Symbols, so those tables share
// these keys but nothing else.

import {
  defaultChannels,
  messagingChannels,
  type ThreadActivityState,
  type ThreadChannel,
  type ThreadStatus,
} from "@/types/thread.ts";

/** The available thread filter views on the home screen. */
export type ThreadFilterMode = "active" | "routines" | "all" | "archived" | "external";

/** Every mode, in the order the filter menus list them. */
export const threadFilterModes: ThreadFilterMode[] = [
  "active",
  "routines",
  "all",
  "archived",
  "external",
];

/** The full name of a view, as its filter control reads. */
export const filterLabels: Record<ThreadFilterMode, string> = {
  active: "Active threads",
  routines: "Routines",
  all: "All threads",
  archived: "Archived",
  external: "External",
};

/**
 * The caption over a view's unpinned group. Shorter than {@link filterLabels}
 * because it sits below a control already reading "Active threads" — repeating
 * the noun would say "threads" twice in one column of a narrow list.
 */
export const groupLabels: Record<ThreadFilterMode, string> = {
  active: "Active",
  routines: "Routines",
  all: "All",
  archived: "Archived",
  external: "External",
};

/** The thread-list query params a filter view reads. */
export type ThreadFilterParams = {
  status: ThreadStatus[];
  channel: ThreadChannel[];
  activityState: ThreadActivityState[];
  labels: string[];
};

/**
 * Maps a filter mode to the API query params needed to fetch the right threads.
 * All filtering is done server-side via status, activityState, channel, and labels.
 *
 * The standard views list `defaultChannels`: in-app threads plus the user's
 * email-only conversations with okthx, which include a routine run the user
 * answered by email. When `includeExternalChannels` is set (the
 * `visible_external_threads` gate), Active / All / Archived also fold in
 * messaging-channel threads (SMS, Slack, Teams, WhatsApp), so external
 * conversations show up in the normal places. `routines` never takes messaging
 * channels (routine runs never move onto one), and the dedicated `external`
 * view stays external-only regardless of the gate.
 */
export const getThreadFilterParams = (
  mode: ThreadFilterMode,
  includeExternalChannels = false,
): ThreadFilterParams => {
  const standardChannels: ThreadChannel[] = includeExternalChannels
    ? [...defaultChannels, ...messagingChannels]
    : defaultChannels;
  switch (mode) {
    case "active":
      return {
        // Paused threads carry a `to-review`/`in-progress` status now
        // (is_paused is orthogonal — BIG-5169), so they're already included.
        status: ["in-progress", "to-review"],
        channel: standardChannels,
        activityState: [],
        labels: [],
      };
    case "routines":
      // Routine runs should surface whether they're still working or have
      // finished, so include `completed` alongside the active statuses — the
      // `routineRun` label is what scopes this list. `draft` (never-sent) and
      // `dead` (failed/abandoned) stay excluded so they don't clutter the list.
      return {
        status: ["in-progress", "to-review", "completed"],
        channel: defaultChannels,
        activityState: [],
        labels: ["routineRun"],
      };
    case "all":
      return {
        status: ["in-progress", "to-review", "completed"],
        channel: standardChannels,
        activityState: [],
        labels: [],
      };
    case "archived":
      return { status: ["completed"], channel: standardChannels, activityState: [], labels: [] };
    case "external":
      return {
        // The External view shows every external thread regardless of
        // lifecycle — active, waiting, or completed/archived — so it's a
        // complete picture of external conversations. `dead`/`draft` stay
        // excluded as noise, matching the other views.
        status: ["in-progress", "to-review", "completed"],
        // Email is external but not a `messagingChannels` member (like mcp), so
        // list it explicitly to keep the External tab showing email threads.
        channel: [...messagingChannels, "email"],
        activityState: [],
        labels: [],
      };
  }
};
